import type { PinReference_Type, PinReference } from '@zendesk/zqa-services/pins'

import config from '@/config'
import type { Session } from '@/types/session'
import type { PinModalState } from '@/composables/usePinModal'

import { initPendo } from '../pendo'

let analytics: any | undefined

const isBot = navigator.userAgent.startsWith('klaus/monitoring/') || 'Cypress' in window

export const track = (name: string, args?: Record<string, any>) => {
  if (!analytics) return console.info(name, args)
  analytics.track(name, args)
}

export const identify = async (session: Session) => {
  if (!isBot && config.pendoIdentifier) {
    analytics = await initPendo(session)
  }
}

export default {
  appUpdateReload: () => track('App update reload'),
  modalOpened: (title: string) => track('Modal opened', { title }),
  modalClosed: (title: string) => track('Modal closed via Esc', { title }),
  accountConnectionUpdated: (tokenId: number) => track('Connection updated', { tokenId }),
  accountConnectionDeleted: (tokenId: number) => track('Connection deleted', { tokenId }),
  accountConnectionReAuthenticated: (name: string, sourceType: string) =>
    track('Connection re-authenticated successfully', { name, sourceType }),
  assignmentListSorted: (direction: string) => track('Assignments list sorted', { direction }),
  workspaceConnectionAdded: ({ teamId, tokenId, tokenName }: any) =>
    track('Connection successfully added to team', { teamId, tokenId, tokenName }),
  workspaceConnectionFailed: ({ teamId, tokenId, tokenName }: any) =>
    track('Connection add fails', { teamId, tokenId, tokenName }),
  workspaceConnectionDeleted: ({ teamId, tokenId }: any) =>
    track('Connection successfully removed from team', { teamId, tokenId }),
  integrationConnectionAdded: (args: any) => track('Connection added successfully', args),
  integrationConnectionFailed: (args: any) => track('Connection add fails', args),
  connectionAdded: (args: any) => track('Connection added successfully', args),
  connectionFailed: (args: any) => track('Connection add fails', args),
  conversationSelectedWithFilter: (filterId: number, filterName: string, isPublic: boolean) =>
    track('Conversation selected with filter', { filterId, filterName, isPublic }),
  slackRemoved: () => track('Slack integration removed'),
  slackAdded: () => track('Slack integration added'),
  navbarEvent: (event: string, title: string) => track(event, { title }),
  loginProcessStarted: (args: any) => track('Started login process', args),
  loginOrSignupFailed: (err: any) =>
    track('Login/Signup error', {
      message: `Auth0 login/signup error | ${err.code}: ${err.description}`,
    }),
  sendingMagicLinkFailed: (message: string) => track('Cannot send magic link', { message }),
  magicLinkSent: (email: string) => track('Magic link sent', { email }),
  passwordResetStarted: (email: string) => track('Started reset password process', { email }),
  sendingResetEmailFailed: (message: string) => track('Cannot send reset email', { message }),
  passwordResetSucceeded: (email: string) => track('Reset password success', { email }),
  filterChanged: ({ editModal, isPublic, name }: any) =>
    track('Filter ' + (editModal ? 'updated' : 'added'), { isPublic, name }),
  filterCloned: (filterId: number, name: string, isPublic: boolean) =>
    track('Filter cloned', { filterId, isPublic, name }),
  filterDeleted: (filterId: number, isPublic: boolean) => track('Filter deleted', { isPublic, filterId }),
  goalUpdated: (goal: number, goalType: string) => track('Goal updated', { goal, goalType }),
  shortcutUsed: (title: string) => track('Shortcut used', { title }),
  userRemoved: ({ removerId, removerEmail, removedUsers }: any) =>
    track('User removed', { removerId, removerEmail, removedUsers }),
  ratingCategoryClicked: ({ categoryId, rating }: any) => track('Clicked rating category', { categoryId, rating }),
  newTeamAdded: ({ teamName, teamId }: any) => track('New team added', { teamName, teamId }),
  reviewUpdated: () => track('Review updated', { addedRatings: true, addedComment: false, addedTags: false }),
  reviewMarkedAsRead: (type: string) => track('Review Marked as Read', { type }),
  teamSelectionClicked: (teamId: number) => track('Clicked team selection', { title: 'Switch team', teamId }),
  logInClicked: () => track('Clicked Log in button'),
  magicLinkLoginClicked: () => track('Clicked Log in via Magic Link button'),
  sendMagicLinkClicked: () => track('Clicked Send magic link button'),
  resetPasswordButtonClicked: () => track('Clicked Reset password button'),
  closeMessageClicked: () => track('Clicked message close button', {}),
  ticketListSorted: (sortOption: string) => track('Sorted ticket list', { sortOption, newNavEnabled: true }),
  pinCreated: ({ referenceType, referenceValue, body }: PinModalState) => {
    const query = new URLSearchParams(referenceValue)
    track('Pin created', { type: referenceType, dashboardCard: query.get('cardId'), hasComment: !!body })
  },
  pinEdited: ({ type, value }: PinReference) => {
    const query = new URLSearchParams(value)
    track('Pin edited', { type, dashboardCard: query.get('cardId') })
  },
  pinDeleted: ({ type, value }: PinReference) => {
    const query = new URLSearchParams(value)
    track('Pin deleted', { type, dashboardCard: query.get('cardId') })
  },
  pinCommentCreated: (isReply: boolean, isPinCreator: boolean) =>
    track('Pin comment created', { isReply, isPinCreator }),
  pinCommentEdited: (isReply: boolean, isPinCreator: boolean) => track('Pin comment edited', { isReply, isPinCreator }),
  pinCommentDeleted: (isReply: boolean, isPinCreator: boolean) =>
    track('Pin comment deleted', { isReply, isPinCreator }),
  pinFolderListSorted: (direction: string) => track('Pin folder list sorted', { direction }),
  pinsSearched: () => track('Pins searched'),
  pinParticipantsAdded: ({
    count,
    addedVia,
    isPinCreator,
  }: {
    count: number
    addedVia: 'new_pin' | 'new_comment'
    isPinCreator: boolean
  }) => track('Pin participants added', { count, addedVia, isPinCreator }),
  pinParticipantRemoved: () => track('Pin participant removed'),
  pinsLeftSidebarClicked: (type: 'message' | 'folder') => track('Pin left sidebar clicked', { type }),
  pinClicked: (isPinCreator: boolean) => track('Pin clicked', { isPinCreator }),
  pinsShowSimilar: (type: PinReference_Type, dashboardCard?: boolean) =>
    track('Pins: show similar', { type, dashboardCard }),
  pinReferencePreviewClicked: (type: PinReference_Type) => track('Pin reference preview clicked', { type }),
  quizListClicked: (quizId: string) => track('Quiz list: opened', { quizId }),
  quizListEdited: (quizId: string) => track('Quiz list: edited', { quizId }),
  quizListDuplicated: () => track('Quiz list: duplicated'),
  quizListDeleted: () => track('Quiz list: deleted'),
  quizListLinkCopied: () => track('Quiz list: link copied'),
  quizListArchived: () => track('Quiz list: archived'),
  quizListUnarchived: () => track('Quiz list: unarchived'),
  quizSaved: () => track('Quiz: draft saved'),
  quizPublished: () => track('Quiz: published'),
  quizDuplicated: () => track('Quiz: duplicated'),
  quizDeleted: () => track('Quiz: deleted'),
  quizLinkCopied: () => track('Quiz: link copied'),
  quizAnswered: () => track('Quiz: answered'),
  quizArchived: () => track('Quiz: archived'),
  quizUnarchived: () => track('Quiz: unarchived'),
  coachingOpenCoacheePicker: (source: string) => track('Coaching: open coachee picker', { source }),
  coachingCreateThread: () => track('Coaching: create session thread'),
  coachingRemoveThread: () => track('Coaching: remove session thread'),
  coachingNewSession: () => track('Coaching: new session'),
  coachingExpandSession: () => track('Coaching: expand session'),
  coachingCopySessionLink: () => track('Coaching: copy session link'),
  coachingStartRenameSession: () => track('Coaching: start rename session'),
  coachingRenamedSession: () => track('Coaching: renamed session'),
  coachingRemoveSession: () => track('Coaching: remove session'),
  coachingScheduleSession: () => track('Coaching: schedule session'),
  coachingEditTalkingPoints: () => track('Coaching: edit session talking points'),
  coachingAddActionItem: () => track('Coaching: add action item'),
  coachingEditActionItem: () => track('Coaching: edit action item'),
  coachingRemoveActionItem: () => track('Coaching: remove action item'),
  coachingMarkActionItemDone: () => track('Coaching: mark action item done'),
  coachingMarkActionItemUndone: () => track('Coaching: mark action item undone'),
  coachingEditPrivateNote: () => track('Coaching: edit private note'),
  coachingOpenSidebarTab: (tab: 'metrics' | 'feedback' | 'pins') => track('Coaching: open sidebar tab', { tab }),
  coachingOpenMetricsLink: (type: 'iqs' | 'dashboard' | 'dashboard_category' | 'quiz' | 'csat') =>
    track('Coaching: open sidebar metrics link', { type }),
  coachingOpenFeedbackConversation: () => track('Coaching: open feedback conversation'),
  coachingOpenPin: (source: 'tab' | 'session') => track('Coaching: open pin', { source }),
  coachingAddPin: () => track('Coaching: add pin to session'),
  coachingRemovePin: () => track('Coaching: remove pin from session'),
  // disputes
  disputeStartReview: () => track('Dispute started: review'),
  disputeStartReviewee: () => track('Dispute started: reviewee'),
  disputeStartEdit: () => track('Dispute started: edit'),
  disputeStartApprove: () => track('Dispute started: approve'),
  disputeStartReject: () => track('Dispute started: reject'),
  disputeDiscard: () => track('Dispute discarded'),
  disputeDelete: () => track('Dispute deleted'),
  disputeSubmit: ({ count, ratingsChanged }: { count: number; ratingsChanged: boolean }) =>
    track('Dispute submit: new', { count, ratingsChanged }),
  disputeSubmitEdit: () => track('Dispute submit: edit'),
  disputeSubmitApproveReview: () => track('Dispute submit: approve review'),
  disputeSubmitPartiallyApproveReview: () => track('Dispute submit: partially approve review'),
  disputeSubmitApproveReviewee: () => track('Dispute submit: approve reviewee'),
  disputeSubmitRejectReview: () => track('Dispute submit: reject review'),
  disputeSubmitRejectReviewee: () => track('Dispute submit: reject reviewee'),
  disputeViewed: () => track('Dispute viewed'),

  // Advanced scorecard events
  reviewSubmitClickedScorecardHasSystemAutoqaCat: () => track('review_submit_clicked_scorecard_has_system_AUTOQA_cat'),
  reviewSubmitClickedScorecardHasCustomPromptCat: () => track('review_submit_clicked_scorecard_has_custom_prompt_cat'),

  // Bot events
  reviewSubmitClickedScorecardHasSystemAutoqaCatAndBotExists: () =>
    track('ZQA - Review button submit with AutoQA system category and bot exists'),
  reviewSubmitClickedScorecardHasCustomPromptCatAndBotExists: () =>
    track('ZQA - Review button submit with custom prompt category and bot exists'),
  botFilterExists: () => track('Conversation filter added: Bot filter'),
  botReplyFilterExists: () => track('Conversation filter added: Bot reply filter'),
}
